
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import ChatbotCard from '@/components/chat/ChatbotCard';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Clock, MessageSquare, UserRound, LogOut, Settings } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { ChatbotData } from '@/components/chat/chatTypes';

interface Conversation {
  id: string;
  botId: string;
  botName: string;
  date: string;
  preview: string;
}

const Dashboard = () => {
  const [username, setUsername] = useState('');
  const [isAdmin, setIsAdmin] = useState(true); // In a real app, this would be a proper auth check
  const [chatbots, setChatbots] = useState<ChatbotData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  
  useEffect(() => {
    // Check if user is authenticated
    const userToken = localStorage.getItem('userToken');
    const userName = localStorage.getItem('userName');
    
    if (!userToken) {
      // Redirect to login if not authenticated
      navigate('/login');
    } else {
      setUsername(userName || 'User');
    }
  }, [navigate]);
  
  // Fetch recommended agents from Supabase
  useEffect(() => {
    const fetchAgents = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('agents')
          .select('*')
          .limit(3);  // Just get top 3 for recommendations
        
        if (error) {
          throw error;
        }
        
        // Transform data to match ChatbotData interface
        const formattedChatbots: ChatbotData[] = data.map((agent: any) => ({
          id: agent.id,
          name: agent.name,
          description: agent.description,
          tags: agent.tags,
          avatarUrl: agent.avatar_url,
          conversations: agent.conversations
        }));
        
        setChatbots(formattedChatbots);
      } catch (error) {
        console.error('Error fetching agents:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchAgents();
  }, []);
  
  const handleSignOut = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userName');
    navigate('/');
  };
  
  // Recent conversations data - matched to the agents we have
  const recentConversations: Conversation[] = [
    { id: '1', botId: 'washington', botName: 'George Washington', date: '2 hours ago', preview: 'We were discussing the challenges of leadership during the Revolutionary War...' },
    { id: '2', botId: 'jefferson', botName: 'Thomas Jefferson', date: 'Yesterday', preview: 'I explained the principles behind the Declaration of Independence...' },
    { id: '3', botId: 'franklin', botName: 'Benjamin Franklin', date: '3 days ago', preview: 'We explored the virtues of civic engagement and community service...' }
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold">Your Dashboard</h1>
            <p className="text-muted-foreground">
              Welcome back, {username}
            </p>
          </div>
          <div className="flex space-x-2">
            {isAdmin && (
              <Button 
                variant="outline" 
                className="rounded-full" 
                onClick={() => navigate('/admin')}
              >
                <Settings size={16} className="mr-2" />
                Admin
              </Button>
            )}
            <Button 
              variant="outline" 
              className="rounded-full" 
              onClick={handleSignOut}
            >
              <LogOut size={16} className="mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
        
        <Tabs defaultValue="conversations" className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="conversations" className="rounded-full">
              <MessageSquare size={16} className="mr-2" />
              Conversations
            </TabsTrigger>
            <TabsTrigger value="profile" className="rounded-full">
              <UserRound size={16} className="mr-2" />
              Profile
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="conversations" className="space-y-8 animate-fade-in">
            {/* Recent Conversations */}
            <section>
              <h2 className="text-2xl font-display font-bold mb-4">Recent Conversations</h2>
              <div className="grid gap-4">
                {recentConversations.map((conversation) => (
                  <Card key={conversation.id} className="overflow-hidden">
                    <div className="grid md:grid-cols-[1fr_auto] items-center">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium">{conversation.botName}</h3>
                          <span className="text-xs text-muted-foreground flex items-center">
                            <Clock size={12} className="mr-1" />
                            {conversation.date}
                          </span>
                        </div>
                        <p className="text-muted-foreground text-sm line-clamp-1">
                          {conversation.preview}
                        </p>
                      </CardContent>
                      <Button 
                        variant="ghost" 
                        className="m-4 whitespace-nowrap rounded-full"
                        onClick={() => navigate(`/chat/${conversation.botId}`)}
                      >
                        Continue
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </section>
            
            {/* Suggested Chatbots */}
            <section>
              <h2 className="text-2xl font-display font-bold mb-4">Suggested for You</h2>
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
                  <p className="mt-4 text-muted-foreground">Loading recommendations...</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {chatbots.map((chatbot) => (
                    <ChatbotCard
                      key={chatbot.id}
                      id={chatbot.id}
                      name={chatbot.name}
                      description={chatbot.description}
                      tags={chatbot.tags}
                      avatarUrl={chatbot.avatarUrl}
                      conversations={chatbot.conversations}
                    />
                  ))}
                </div>
              )}
            </section>
          </TabsContent>
          
          <TabsContent value="profile" className="animate-fade-in">
            <Card>
              <CardHeader>
                <h2 className="text-2xl font-display font-bold">Your Bluesky Profile</h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="font-medium block mb-1">Username</label>
                  <p className="text-muted-foreground">{username}</p>
                </div>
                <div>
                  <label className="font-medium block mb-1">Connected Account</label>
                  <p className="text-muted-foreground">Bluesky</p>
                </div>
                <div>
                  <label className="font-medium block mb-1">Conversations</label>
                  <p className="text-muted-foreground">{recentConversations.length} recent conversations</p>
                </div>
                
                <div className="pt-4">
                  <Button 
                    variant="outline" 
                    className="rounded-full w-full md:w-auto text-destructive hover:bg-destructive/10"
                    onClick={handleSignOut}
                  >
                    <LogOut size={16} className="mr-2" />
                    Sign Out
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Dashboard;
